import Checkbox from '@mui/material/Checkbox'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import upperCase from 'lodash/upperCase'
import React, { useCallback } from 'react'
import { useSelector } from 'react-redux'

import { ReactComponent as CalendarDayIcon } from 'assets/CalendarDay.svg'
import { ReactComponent as CalendarMoneyIcon } from 'assets/CalendarMoney.svg'
import { ReactComponent as CalendarXMarkIcon } from 'assets/CalendarXMark.svg'
import { METHOD_TO_ICON_MAP } from 'components/common/chip/MethodChip'
import ContainersChipBase from 'components/configured-service/common/ContainersChip'
import ServiceDetailChipBase from 'components/configured-service/layout/ServiceDetailChip'
import Tag from 'components/tags/Tag'
import MeasureChip from 'containers/new-route-manager/RouteCard/MeasureChip'
import { selectIsInventoryFeatureEnabled } from 'data/tenant/tenantSettingsSelectors'
import T from 'T'
import { formatDateStringToFE } from 'utils/date'
import { getMeasureUnit } from 'utils/get-measure-unit'
import { formatCurrency } from 'utils/price'
import { getPricingPeriod, getServiceRecurrenceText } from 'utils/service'

import { hasEnded, isDeactivatedService } from './reactivationSelection'
import ReactivationServiceDays from './ReactivationServiceDays'

import type { ReactivationLocationGroup, ReactivationSelection, ReactivationServiceRow as ServiceRowData } from './reactivationSelection'

type ReactivationServiceRowProps = {
  group: ReactivationLocationGroup
  service: ServiceRowData
  selection: ReactivationSelection
  onToggleService: (group: ReactivationLocationGroup, serviceId: string) => void
}

// Untyped .jsx chips shared with the Services tab, cast at the module boundary so this .tsx can render them.
const ContainersChip = ContainersChipBase as React.ElementType
const ServiceDetailChip = ServiceDetailChipBase as React.ElementType

const pricingPeriodOf = ({ pricingPeriodPer, pricingPeriodInterval }: ServiceRowData) =>
  pricingPeriodPer && pricingPeriodInterval && pricingPeriodInterval !== 'Instance'
    ? getPricingPeriod(pricingPeriodPer, pricingPeriodInterval)
    : ''

const ReactivationServiceRow = ({ group, service, selection, onToggleService }: ReactivationServiceRowProps) => {
  const isInventoryFeatureEnabled = useSelector(selectIsInventoryFeatureEnabled)
  const handleChange = useCallback(() => onToggleService(group, service.configuredServiceId), [group, service, onToggleService])
  const isDeactivated = isDeactivatedService(service)
  const pricingPeriod = pricingPeriodOf(service)
  const containerCount = (isInventoryFeatureEnabled ? service.newContainerCount : service.quantity) ?? 0
  const MethodIcon = METHOD_TO_ICON_MAP[upperCase(service.methodType ?? '')]

  return (
    <Stack alignItems="flex-start" direction="row" gap={1} pl={1} py={1} sx={{ '& + &': { borderTop: 1, borderColor: 'divider' } }}>
      <Checkbox
        checked={!isDeactivated || selection.serviceIds.has(service.configuredServiceId)}
        disabled={!isDeactivated}
        inputProps={{ 'aria-label': service.serviceName ?? '' }}
        size="small"
        onChange={handleChange}
      />
      <Stack flex={1} gap={0.75} minWidth={0}>
        <Stack alignItems="center" direction="row" gap={1}>
          {MethodIcon && <MethodIcon height={24} style={{ minWidth: 24 }} width={24} />}
          <Typography fontWeight={600} variant="body2">
            {service.serviceName}
          </Typography>
          {service.servicePrice != null && (
            <Typography variant="body2">
              {formatCurrency(service.servicePrice)}
              {pricingPeriod && ` / ${pricingPeriod}`}
            </Typography>
          )}
        </Stack>
        <Stack alignItems="center" direction="row" flexWrap="wrap" gap={1}>
          <Typography variant="body2">{getServiceRecurrenceText(service)}</Typography>
          <ContainersChip label={`${T.QUANTITY} | ${containerCount}`} size="small" variant="outlined" />
          {service.measureUnit && (
            <MeasureChip label={`${service.measureVolume ?? 0} ${getMeasureUnit(service.measureUnit)}`} measureUnit={service.measureUnit} />
          )}
          <ReactivationServiceDays daysOfWeek={service.daysOfWeek} />
        </Stack>
        <Stack alignItems="center" direction="row" flexWrap="wrap" gap={0.5}>
          <ServiceDetailChip
            IconComponent={CalendarDayIcon}
            label={formatDateStringToFE(service.startDate ?? '')}
            paletteColor="primary"
            paletteVariant="dark"
            tooltip={T.START_DATE}
          />
          <ServiceDetailChip
            IconComponent={CalendarMoneyIcon}
            label={formatDateStringToFE(service.billingStartDate ?? '')}
            paletteColor="primary"
            paletteVariant="dark"
            tooltip={T.BILLING_START_DATE}
          />
          <ServiceDetailChip
            IconComponent={CalendarMoneyIcon}
            label={formatDateStringToFE(service.billingEndDate ?? '')}
            paletteColor="error"
            tooltip={T.BILLING_END_DATE}
          />
          {isDeactivated && hasEnded(service) && (
            <ServiceDetailChip
              IconComponent={CalendarXMarkIcon}
              label={formatDateStringToFE(service.endDate ?? '')}
              paletteColor="error"
              tooltip={T.END_DATE}
            />
          )}
          {(service.tags ?? []).map(tag => (
            <Tag key={tag.id} clickable={false} tag={tag} />
          ))}
        </Stack>
      </Stack>
    </Stack>
  )
}

export default ReactivationServiceRow
